import { useState, useEffect } from "react";

import {
    useProject,
    useTask,
    useInspectionAttributes,
    useDocument,
    useLead,
    useMilestone,
} from "../../../../../hooks/query";
import { useAuth } from "../../../../../hooks/useAuth";

import { MILESTONE_KEY } from "../../../../../constants/milestone";
import {
    buildRoofMeasurementAttributes,
    buildRoofPitchAttributeFromPitchTable,
} from "../../../../../utils/eagleViewMeasurements";
import { useNetworkStatus } from "../../../../../hooks/useNetworkStatus";
import { offlineStore } from "../../../../../utils/offlineStore";

export default function useCreateProjectHandler() {
    const { useCreateProject, useGetEagleViewReport, useRevertProject } =
        useProject();
    const { useCreate: useCreateTask } = useTask();
    const { useCreate: useCreateAttribute } = useInspectionAttributes();
    const { useInitialCreate: useCreateDocument } = useDocument();
    const { useCreateLead } = useLead();
    const { useUpsertMilestone } = useMilestone();

    const createProjectSvc = useCreateProject();
    const revertProjectSvc = useRevertProject();
    const readEagleViewReportSvc = useGetEagleViewReport();
    const createTaskSvc = useCreateTask();
    const createAttributeSvc = useCreateAttribute();
    const createDocumentSvc = useCreateDocument();
    const createLeadSvc = useCreateLead();
    const upsertMilestoneSvc = useUpsertMilestone();
    const { user, selectedOrganization } = useAuth();
    const isOnline = useNetworkStatus();

    const [progress, setProgress] = useState({
        loading: false,
        message: "",
        error: null,
        state: 0,
    });

    const useInitializeForm = (setValue: any, getValues: any) => {
        useEffect(() => {
            const currentSalesman = getValues("salesmanName");
            if (!currentSalesman && user?.Email) {
                const fullName = [user.FirstName, user.LastName]
                    .filter(Boolean)
                    .join(" ")
                    .trim();

                setValue("salesmanName", {
                    value: user.Email,
                    label: fullName || user.Email,
                    ...user,
                });
// eslint-disable-next-line react-hooks/exhaustive-deps

            }
        }, [user, setValue, getValues]);
    };

    const readEagleViewReport = async (reportId: string) => {
        if (!reportId) {
            return;
        }
        const response = await readEagleViewReportSvc.mutateAsync({
            ReportId: reportId,
        });
        return response;
    };

    const createMeasurements = async (measurements: any, projectId: string) => {
        const measurementsObj = buildRoofMeasurementAttributes(measurements);

        for (const [key, value] of Object.entries(measurementsObj)) {
            await createAttributeSvc.mutateAsync({
                ProjectId: projectId,
                AttributeName: key,
                Value: value,
            });
        }
    };

    const createProject = async (
        data: any,
        hasEagleViewReport: boolean = false,
    ) => {
        // Note: EagleViewStatus and InsuranceStatus are calculated by the backend,
        // so we don't send them from the frontend

        if (!selectedOrganization?.Id) {
            throw new Error(
                "No organization selected. Choose an organization before creating a project.",
            );
        }

        // Use jobStartDate as StartDate if available, otherwise inspectionDate, otherwise current date
        // StartDate is required by the database, so we need a valid date
        const startDate =
            data.jobStartDate ||
            data.inspectionDate ||
            new Date().toISOString();

        const primarySalesman = data.salesmanName;
        const secondarySalesmanName = data.secondarySalesmanName;

        const params = {
            Name: data.name,
            OrganizationId: selectedOrganization.Id,
            Description: data.description,
            ProjectType: data.type,
            EagleViewOrderId: data.evorderid || "",
            // Store user IDs for tracking (new ID-based columns)
            PrimarySalesmanId:
                primarySalesman?.Id || primarySalesman?.value || "",
            SecondarySalesmanName: secondarySalesmanName || "",
            StartDate: startDate,
            CustomerName: data.customerName,
            CustomerPhoneNumber: data.customerPhoneNumber,
            CustomerEmail: data.customerEmail || "",
            CustomerStreet: data.customerStreetAddress,
            CustomerCity: data.customerCity,
            CustomerState: data.customerState,
            CustomerZipCode: data.customerZipCode,
            CustomerCountry: data.customerCountry,
            PropertyStreet: data.propertyStreetAddress,
            PropertyCity: data.propertyCity,
            PropertyState: data.propertyState,
            PropertyZipCode: data.propertyZipCode,
            PropertyCountry: data.propertyCountry,
            InsuranceProvider: data.insuranceProvider || "",
            InsuranceClaimNumber: data.claimNumber || "",
            InsurancePolicyNumber: data.policyNumber || "",
            InsuranceAgentContact: "", // Not in form, but expected by backend
            InsuranceAgentNumber: data.adjusterContact || "",
            AdjusterContact: data.adjusterContact || "",
            AdjusterEmail: data.adjusterEmail || "",
            InsuranceAgentEmail: data.adjusterEmail || "",
            HasEagleViewReport: hasEagleViewReport,
            NoEagleViewId: data.noEagleViewId || false,
            NoInsurance: data.noInsurance || false,
        };
        // create project
        const response = await createProjectSvc.mutateAsync(params);
        return response;
    };

    const createAttributes = async (
        projectId: string,
        attributes: any = {},
    ) => {
        for (const [key, value] of Object.entries(attributes)) {
            await createAttributeSvc.mutateAsync({
                ProjectId: projectId,
                AttributeName: key,
                Value: String(value),
            });
        }
    };

    const createMilestones = async (data: any, projectId: string) => {
        const milestones = [
            { key: MILESTONE_KEY.INSPECTION, date: data.inspectionDate },
            { key: MILESTONE_KEY.ESTIMATE, date: data.estimateDate },
            {
                key: MILESTONE_KEY.PERMIT_SUBMISSION,
                date: data.permitSubmissionDate,
            },
            {
                key: MILESTONE_KEY.MATERIAL_PURCHASE,
                date: data.materialPurchaseDate,
            },
            { key: MILESTONE_KEY.JOB_START, date: data.jobStartDate },
            {
                key: MILESTONE_KEY.ESTIMATED_COMPLETION,
                date: data.estimatedEndDate,
            },
        ];

        for (const milestone of milestones) {
            if (milestone.date) {
                await upsertMilestoneSvc.mutateAsync({
                    ProjectId: projectId,
                    MilestoneKey: milestone.key,
                    PlannedDate: milestone.date,
                    ActualDate: null,
                    Status: "PENDING",
                });
            }
        }
    };

    const createDocuments = async (projectId: string) => {
        const params = {
            ProjectId: projectId,
        };
        await createDocumentSvc.mutateAsync(params);
    };

    const createTasks = async (projectId: string) => {
        for (const task of standardTasks) {
            const taskParams = {
                ProjectId: projectId,
                ParentTaskId: null,
                Name: task.name,
                Status: task.status,
                SortOrder: task.order,
            };
            await createTaskSvc.mutateAsync(taskParams);
        }
    };

    const createProjectEventHandler = async (
        data: any,
        options?: { forceOnline?: boolean },
    ) => {
        // Sync must never re-queue offline — force the online create path.
        if (!options?.forceOnline && !isOnline) {
            setProgress({ loading: true, message: "Saving project offline...", error: null, state: 0 });
            const offlineId = "offline-proj-" + Date.now().toString(36) + Math.random().toString(36).substr(2);
            await offlineStore.saveProject({
                offlineId,
                payload: data,
                timestamp: Date.now(),
                synced: false
            });
            setProgress({ loading: false, message: "Project saved offline", error: null, state: 7 });
            return { projectId: offlineId };
        }

        if (options?.forceOnline && typeof navigator !== "undefined" && !navigator.onLine) {
            throw new Error("Cannot sync project while the browser reports offline");
        }

        let activeProjectId: string | null = null;
        try {
            let measurements = null;
            let hasEagleViewReport = false;
            const hasEagleViewId =
                data.evorderid &&
                data.evorderid.trim() !== "" &&
                !data.noEagleViewId;

            let response = null;
            if (hasEagleViewId) {
                // Step 1: Load EagleView report
                setProgress({
                    loading: true,
                    message: progressStages[ProgressState.LoadingReport],
                    error: null,
                    state: ProgressState.LoadingReport,
                });

                try {
                    response = await readEagleViewReport(data.evorderid);
                    if (!response?.TotalMeasurements) {
                        throw new Error(
                            "The EagleView report was found, but it contains no measurement data. Please check the Order ID.",
                        );
                    }
                    hasEagleViewReport = true;

                    // Step 2: Report loaded
                    setProgress({
                        loading: true,
                        message: progressStages[ProgressState.ReportLoaded],
                        error: null,
                        state: ProgressState.ReportLoaded,
                    });
                } catch (error: any) {
                    // Re-throw to be caught by the main catch block and stop the process
                    const errorMsg =
                        error.message ||
                        "Failed to retrieve the EagleView report.";
                    throw new Error(
                        `EagleView Error: ${errorMsg} Project creation aborted.`,
                    );
                }
            } else {
                // Skip EagleView report loading
                setProgress({
                    loading: true,
                    message:
                        "Preparing to create project with manual measurements...",
                    error: null,
                    state: ProgressState.ReportLoaded,
                });
            }

            // Step 3: Create project
            const projectDetails = await createProject(
                data,
                hasEagleViewReport,
            );
            const projectId = projectDetails.Id as string;
            activeProjectId = projectId;

            setProgress({
                loading: true,
                message: progressStages[ProgressState.ProjectCreated],
                error: null,
                state: ProgressState.ProjectCreated,
            });

            const leadSource = data.leadSource;

            if (leadSource) {
                // Use secondary lead name if provided, otherwise fallback to primary lead source name
                const leadName =
                    data.secondaryLead || leadSource.Name || leadSource.label;

                // ID will be generated in the backend
                await createLeadSvc.mutateAsync({
                    LeadSourcesId: leadSource.Id || leadSource.value,
                    Name: leadName,
                    ProjectId: projectId,
                });
            }

            // Step 4: Create milestones (dates)
            await createMilestones(data, projectId);

            setProgress({
                loading: true,
                message: progressStages[ProgressState.MilestonesCreated],
                error: null,
                state: ProgressState.MilestonesCreated,
            });

            // Step 5: Create tasks
            await createTasks(projectId);

            setProgress({
                loading: true,
                message: progressStages[ProgressState.TasksCreated],
                error: null,
                state: ProgressState.TasksCreated,
            });

            // Step 6: Load measurements (from EagleView or manual entry)
            let pitchAttribute: string | null = null;
            if (hasEagleViewId && response) {
                pitchAttribute = buildRoofPitchAttributeFromPitchTable(
                    response.PitchTable,
                );

                const measurements = response.TotalMeasurements;
                if (measurements) {
                    await createMeasurements(measurements, projectId);
                }
            } else if (data.noEagleViewId && !data.skipMeasurements) {
                // Use manual measurements from form
                await createMeasurements(
                    {
                        AreaValue: data.measurementArea,
                        LengthRidge: data.measurementRidge,
                        LengthHip: data.measurementHip,
                        LengthValley: data.measurementValley,
                        LengthEave: data.measurementEave,
                        LengthRake: data.measurementRake,
                    },
                    projectId,
                );
            }

            setProgress({
                loading: true,
                message: progressStages[ProgressState.MeasurementsLoaded],
                error: null,
                state: ProgressState.MeasurementsLoaded,
            });

            const attributes = pitchAttribute
                ? { RoofPitch: pitchAttribute }
                : {};
            await createAttributes(projectId, attributes);

            setProgress({
                loading: true,
                message: progressStages[ProgressState.AttributesCreated],
                error: null,
                state: ProgressState.AttributesCreated,
            });

            await createDocuments(projectId);

            setProgress({
                loading: false,
                message: progressStages[ProgressState.DocumentCreated],
                error: null,
                state: ProgressState.DocumentCreated,
            });

            return { projectId };
        } catch (e: any) {
            // Revert changes if project was created but something else failed
            if (activeProjectId) {
                console.log(
                    `Reverting partial project creation for ID: ${activeProjectId}`,
                );
                try {
                    await revertProjectSvc.mutateAsync(activeProjectId);
                } catch (revertError) {
                    console.error(
                        "Critical: Failed to revert partial project creation",
                        revertError,
                    );
                }
            }

            setProgress((prev) => ({
                ...prev,
                loading: false,
                error: e,
            }));
            console.error(e);
            return { projectId: null };
        }
    };

    return { createProjectEventHandler, progress, useInitializeForm };
}

const ProgressState = {
    LoadingReport: 0,
    ReportLoaded: 1,
    ProjectCreated: 2,
    MilestonesCreated: 3,
    TasksCreated: 4,
    MeasurementsLoaded: 5,
    AttributesCreated: 6,
    DocumentCreated: 7,
} as const;

const progressStages = [
    "Loading EagleView report... warming up the satellites.",
    "Report loaded successfully. Everything’s still in one piece.",
    "Project created! Now generating project milestones...",
    "Milestones generated. Setting up your workflow tasks...",
    "Tasks ready. Fetching measurements... accuracy is our middle name.",
    "Measurements are calculated. Creating attributes... naming things is harder than it looks.",
    "Documents templates are generating! This part takes a little brainpower.",
    "All set! Redirecting to your new project. Try not to get whiplash.",
];

const standardTasks = [
    {
        name: "Initial Inspection",
        status: "IN-PROGRESS",
        order: 1,
    },
    {
        name: "Estimate/Contract",
        status: "NOT-STARTED",
        order: 2,
    },
    {
        name: "Contract Signing",
        status: "NOT-STARTED",
        order: 3,
    },
    {
        name: "Building Permit",
        status: "NOT-STARTED",
        order: 4,
    },
    {
        name: "Material Ordering",
        status: "NOT-STARTED",
        order: 5,
    },
    {
        name: "Job Execution",
        status: "NOT-STARTED",
        order: 6,
    },
    {
        name: "Final Settlement",
        status: "NOT-STARTED",
        order: 7,
    },
];
